import cloneDeep from 'lodash.clonedeep';

import {
  CreateScribeFromTemplateSuccessAction,
  CREATE_SCRIBE_FROM_TEMPLATE_SUCCESS
} from 'js/actionCreators/templatesActions';
import { ExistingScribeModel, ScribeScene, ScribeState, TemporaryDuplicatedScribe } from 'js/types';
import { getActiveScene } from 'js/shared/helpers/scenesHelpers';
import { updateCameraCoverage } from 'js/sagas/sagaHelpers/updateCameraCoverage';

import {
  UPDATE_SCRIBE,
  SET_ACTIVE_SCRIBE,
  SET_ACTIVE_ELEMENTS,
  ADD_SCRIBE_SUCCESS,
  LOAD_SCRIBES,
  LOAD_SCRIBES_SUCCESS,
  LOAD_MORE_SCRIBES,
  LOAD_MORE_SCRIBES_SUCCESS,
  LOAD_ALL_PROJECTS,
  LOAD_ALL_PROJECTS_SUCCESS,
  DELETE_SCRIBE_SUCCESS,
  SORT_SCRIBES,
  ADD_TO_ACTIVE_ELEMENTS,
  PREP_SCRIBE_FOR_EDITOR_SUCCESS,
  CREATE_SCENE,
  SET_SELECTED_SCENES,
  SET_ACTIVE_SCENE,
  DELETE_MULTIPLE_ELEMENTS_BY_ID,
  DELETE_SELECTED_SCENES,
  REWRITE_HISTORY,
  ADD_TO_SELECTED_SCENES,
  UPDATE_LAST_SELECTED_SCENE,
  TOGGLE_ACTIVE_ELEMENT,
  UpdateScribeAction,
  SetActiveScribeAction,
  SetActiveElementsAction,
  ToggleActiveElementAction,
  AddToActiveElementsAction,
  AddScribeSuccessAction,
  LoadScribesSuccessAction,
  LoadMoreScribesSuccessAction,
  DeleteScribeSuccessAction,
  SortScribeAction,
  UpdateUndoRedoHistoryAction,
  PrepScribeForEditorSuccessAction,
  SetSelectedScenesAction,
  SetActiveSceneAction,
  AddToSelectedScenesAction,
  UpdateLastSelectedSceneAction,
  CREATE_ELEMENT,
  CreateElementAction,
  UPDATE_PROJECT_TITLE,
  UpdateProjectTitleAction,
  UPDATE_PROJECT_TITLE_FAILED,
  UpdateProjectTitleFailedAction,
  LOAD_SCRIBE_BY_ID_SUCCESS,
  LoadScribeByIdSuccessAction,
  FilterByCanvasSizeAction,
  FILTER_SCRIBES_BY_CANVAS_SIZE,
  FILTER_SCRIBES_BY_SEARCH_TERM,
  UpdateProjectSearchTermAction,
  FilterByStatusAction,
  FILTER_SCRIBES_BY_STATUS,
  SET_SELECTED_AUDIO_CLIP,
  SetSelectedAudioClipAction,
  SET_FIRST_LOAD,
  SetFirstLoadAction,
  SET_SELECTED_TEXT_COLOR,
  SetSelectedTextColorAction,
  SetCustomTextStyleAction,
  SET_CUSTOM_TEXT_STYLE,
  SetIsTextboxEditingAction,
  SET_IS_TEXTBOX_EDITING,
  RangeSelectElementsAction,
  RANGE_SELECT_ELEMENTS,
  UPDATE_SCRIBE_SUCCESS,
  UpdateScribeSuccessAction
} from '../actionCreators/scribeActions';
import {
  GET_VIDEO_DOWNLOAD_URL_SUCCESS,
  GetVideoDownloadUrlSuccessAction
} from '../actionCreators/getVideoDownloadUrlSuccessAction';
import {
  EDITOR_UNDO_SUCCESS,
  EDITOR_REDO_SUCCESS,
  EDITOR_SET_EDIT_PANEL_MODE,
  EditorUndoSuccessAction,
  EditorRedoSuccessAction,
  EditorSetEditPanelModeAction,
  EDITOR_CLOSED,
  EditorClosedAction
} from '../actionCreators/editorActions';
import { RESET_STATE } from '../actionCreators/generalActions';
import {
  DUPLICATE_SCRIBE,
  DUPLICATE_SCRIBE_SUCCESS,
  DUPLICATE_SCRIBE_FAILED,
  DUPLICATE_SCRIBE_START,
  DuplicateScribeStartAction,
  DuplicateScribeSuccessAction,
  DuplicateScribeFailedAction
} from '../actionCreators/duplicateScribeActions';

import setActiveSceneReducer from './reducerHelpers/setActiveSceneReducer';
import renameProjectReducer from './reducerHelpers/renameProjectReducer';
import { createHomepageProjectFromProject, replaceHomepageProject } from './reducerHelpers/homepageProjectHelpers';
import { rangeSelectElementsReducer } from './reducerHelpers/rangeSelectElements';
import { setConversionComplete } from './legacyProjectMigration';

const filterTemporaryDuplicateProject = (project: ExistingScribeModel | TemporaryDuplicatedScribe, loadingId: string) =>
  project.loadingId !== loadingId;

const initialState: ScribeState = {
  activeScribePast: [],
  activeScribeFuture: [],
  activeElements: [],
  selectedTextColor: null,
  loadingAllScribes: true,
  loadingScribes: [],
  allScribes: [],
  scribesSortOrder: 'updatedDate',
  loadingScribeForEditor: true,
  selectedSceneIds: [],
  selectedAudioClip: null,
  duplicatingScribe: false,
  firstLoad: true,
  lastSelectedScene: null,
  lastSelectedElement: null,
  canvasSizeFilter: 'allSizes',
  statusFilter: 'anyStatus',
  projectSearchTerm: '',
  homepageProjects: [],
  isTextboxEditing: false,
  customTextStyleToBeSet: null,
  hasMoreProjects: false,
  currentProjectPage: 0,
  totalProjectCount: 0,
  loadingMoreProjects: false,
  allProjectsLoaded: false
};

type ScribeAction =
  | {
      type:
        | typeof RESET_STATE
        | typeof LOAD_SCRIBES
        | typeof LOAD_MORE_SCRIBES
        | typeof LOAD_ALL_PROJECTS
        | typeof LOAD_ALL_PROJECTS_SUCCESS
        | typeof CREATE_SCENE
        | typeof DELETE_SELECTED_SCENES
        | typeof DELETE_MULTIPLE_ELEMENTS_BY_ID
        | typeof DUPLICATE_SCRIBE;
    }
  | EditorClosedAction
  | UpdateScribeAction
  | SetActiveScribeAction
  | SetActiveElementsAction
  | ToggleActiveElementAction
  | AddToActiveElementsAction
  | AddScribeSuccessAction
  | EditorUndoSuccessAction
  | EditorRedoSuccessAction
  | LoadScribesSuccessAction
  | LoadMoreScribesSuccessAction
  | DeleteScribeSuccessAction
  | SortScribeAction
  | UpdateUndoRedoHistoryAction
  | PrepScribeForEditorSuccessAction
  | SetSelectedScenesAction
  | SetActiveSceneAction
  | EditorSetEditPanelModeAction
  | DuplicateScribeStartAction
  | DuplicateScribeSuccessAction
  | DuplicateScribeFailedAction
  | AddToSelectedScenesAction
  | UpdateLastSelectedSceneAction
  | CreateScribeFromTemplateSuccessAction
  | CreateElementAction
  | UpdateProjectSearchTermAction
  | UpdateProjectTitleAction
  | UpdateProjectTitleFailedAction
  | LoadScribeByIdSuccessAction
  | SetSelectedAudioClipAction
  | SetFirstLoadAction
  | FilterByCanvasSizeAction
  | FilterByStatusAction
  | SetSelectedTextColorAction
  | SetCustomTextStyleAction
  | SetIsTextboxEditingAction
  | RangeSelectElementsAction
  | UpdateScribeSuccessAction
  | GetVideoDownloadUrlSuccessAction
  | ReturnType<typeof setConversionComplete>;

export default function scribesReducer(state = initialState, action: ScribeAction): ScribeState {
  switch (action.type) {
    case UPDATE_SCRIBE:
      action.scribe.updatedOn = new Date();

      const newActiveElements = action.activeElementIds ? action.activeElementIds : state.activeElements;
      const newActiveAudioClipId = action.activeAudioClipId ? action.activeAudioClipId : state.selectedAudioClip?.id;

      let scribeToCopyIndex = -1;
      const scribeToCopy = state.allScribes.find((scribe, index) => {
        const isMatch = scribe.id === action.scribe.id;
        if (isMatch) scribeToCopyIndex = index;
        return isMatch;
      });

      if (!scribeToCopy) {
        return state;
      }

      const clonedCurrentScribe = cloneDeep(scribeToCopy);
      const shouldUpdateUndoHistory = !action.skipUndoHistory;
      let scribeToApply = action.scribe;

      const activeScene = scribeToApply.scenes.find(scene => scene.active);
      if (activeScene) scribeToApply = updateCameraCoverage(newActiveElements, activeScene, scribeToApply);

      if (shouldUpdateUndoHistory) {
        clonedCurrentScribe.selectedElementIds = [...state.activeElements];
        clonedCurrentScribe.selectedAudioClipId = state.selectedAudioClip?.id;
        scribeToApply.nextSelectedElementIds = [...newActiveElements];
        scribeToApply.nextSelectedAudioClipId = newActiveAudioClipId;
      }

      const activeScribePast = shouldUpdateUndoHistory
        ? [...state.activeScribePast, clonedCurrentScribe]
        : state.activeScribePast;

      const homeProjectToUpdate = createHomepageProjectFromProject(scribeToApply);
      return {
        ...state,
        allScribes: state.allScribes.toSpliced(scribeToCopyIndex, 1, scribeToApply),
        homepageProjects: replaceHomepageProject(state.homepageProjects, homeProjectToUpdate),
        activeScribePast,
        activeScribeFuture: action.skipUndoHistory ? state.activeScribeFuture : [],
        activeElements: newActiveElements
      };

    case SET_ACTIVE_SCRIBE: {
      return {
        ...state,
        // clear undo/redo history
        activeScribePast: [],
        activeScribeFuture: [],
        activeElements: [],
        loadingScribeForEditor: true,
        lastSelectedScene: null,
        lastSelectedElement: null
      };
    }
    case SET_FIRST_LOAD: {
      return { ...state, firstLoad: action.value };
    }
    case SET_ACTIVE_ELEMENTS: {
      return {
        ...state,
        activeElements: typeof action.elementIds === 'string' ? [action.elementIds] : action.elementIds || [],
        lastSelectedElement: action.elementIds?.length ? action.elementIds : null,
        selectedSceneIds:
          (!action.elementIds || action.elementIds.length === 0) && state.selectedSceneIds.length > 0
            ? state.selectedSceneIds
            : [],
        selectedAudioClip: null,
        lastSelectedScene: null
      };
    }

    case TOGGLE_ACTIVE_ELEMENT: {
      const elementIsAlreadySelected = state.activeElements.includes(action.elementId);

      if (elementIsAlreadySelected) {
        return {
          ...state,
          activeElements: state.activeElements.filter(element => element !== action.elementId)
        };
      } else {
        return {
          ...state,
          activeElements: [...state.activeElements, action.elementId],
          lastSelectedElement: action.elementId
        };
      }
    }

    case ADD_TO_ACTIVE_ELEMENTS: {
      const activeElementsCopy = state.activeElements.slice() as string | Array<string>;

      const newActiveElements = typeof activeElementsCopy === 'string' ? [activeElementsCopy] : activeElementsCopy;

      if (newActiveElements.indexOf(action.elementId) === -1) newActiveElements.push(action.elementId);

      return {
        ...state,
        activeElements: newActiveElements,
        lastSelectedElement: action.elementId
      };
    }

    case RANGE_SELECT_ELEMENTS: {
      return rangeSelectElementsReducer(state, action);
    }

    case RESET_STATE: {
      return {
        ...initialState
      };
    }

    case ADD_SCRIBE_SUCCESS: {
      return {
        ...state,
        allScribes: [...state.allScribes, action.scribe],
        selectedSceneIds: [action.scribe.scenes[0].id],
        lastSelectedScene: action.scribe.scenes[0].id,
        homepageProjects: [createHomepageProjectFromProject(action.scribe), ...state.homepageProjects]
      };
    }

    case EDITOR_UNDO_SUCCESS:
    case EDITOR_REDO_SUCCESS: {
      return {
        ...state,
        activeScribePast: action.newPast,
        activeScribeFuture: action.newFuture,
        activeElements: []
      };
    }

    case LOAD_SCRIBES: {
      return {
        ...state,
        loadingAllScribes: true,
        currentProjectPage: 0,
        hasMoreProjects: false,
        allProjectsLoaded: false
      };
    }

    case LOAD_SCRIBES_SUCCESS: {
      return {
        ...state,
        homepageProjects: action.scribes,
        loadingAllScribes: false,
        hasMoreProjects: action.hasMore,
        totalProjectCount: action.totalCount,
        currentProjectPage: 1,
        allProjectsLoaded: false
      };
    }

    case setConversionComplete.type: {
      return {
        ...state,
        allScribes: [action.payload.convertedProject, ...state.allScribes],
        selectedSceneIds: [action.payload.convertedProject.scenes[0].id],
        lastSelectedScene: action.payload.convertedProject.scenes[0].id,
        homepageProjects: [createHomepageProjectFromProject(action.payload.convertedProject), ...state.homepageProjects]
      };
    }

    case LOAD_MORE_SCRIBES: {
      return {
        ...state,
        loadingMoreProjects: true
      };
    }

    case LOAD_MORE_SCRIBES_SUCCESS: {
      return {
        ...state,
        homepageProjects: [...state.homepageProjects, ...action.scribes],
        loadingMoreProjects: false,
        hasMoreProjects: action.hasMore,
        currentProjectPage: state.currentProjectPage + 1
      };
    }

    case LOAD_ALL_PROJECTS: {
      return {
        ...state,
        loadingMoreProjects: true
      };
    }

    case LOAD_ALL_PROJECTS_SUCCESS: {
      return {
        ...state,
        loadingMoreProjects: false,
        allProjectsLoaded: true
      };
    }

    case DELETE_SCRIBE_SUCCESS: {
      const newScribes = [...state.allScribes];
      const index = newScribes.findIndex(scribe => {
        return scribe.id === action.scribeId;
      });

      if (index > -1) {
        newScribes.splice(index, 1);
      }

      return {
        ...state,
        allScribes: newScribes
      };
    }

    case SORT_SCRIBES:
      return {
        ...state,
        scribesSortOrder: action.sortOrder
      };

    case REWRITE_HISTORY:
      return {
        ...state,
        activeScribePast: action.activeScribePast,
        activeScribeFuture: action.activeScribeFuture
      };

    case PREP_SCRIBE_FOR_EDITOR_SUCCESS: {
      const replacementIndex = state.allScribes.findIndex(scribe => scribe.id === action.scribe.id);
      const activeScene: ScribeScene | undefined = getActiveScene(action.scribe);

      if (replacementIndex !== -1) {
        const updatedScribes = [...state.allScribes];
        updatedScribes[replacementIndex] = action.scribe;

        return {
          ...state,
          allScribes: updatedScribes,
          loadingScribeForEditor: false,
          selectedSceneIds: activeScene ? [activeScene.id] : [],
          lastSelectedScene: activeScene ? activeScene.id : null
        };
      }
      return state;
    }

    case EDITOR_CLOSED: {
      return {
        ...state,
        loadingScribeForEditor: true,
        selectedSceneIds: [],
        lastSelectedScene: null,
        lastSelectedElement: null
      };
    }

    case CREATE_SCENE: {
      return {
        ...state,
        activeElements: []
      };
    }

    case SET_SELECTED_SCENES: {
      if (action.sceneIds.length === 0) {
        return {
          ...state,
          selectedAudioClip: null,
          selectedSceneIds: [],
          lastSelectedScene: null
        };
      } else {
        return {
          ...state,
          selectedSceneIds: [...action.sceneIds],
          activeElements: [],
          lastSelectedScene: action.sceneIds[action.sceneIds.length - 1] || null
        };
      }
    }

    case SET_SELECTED_AUDIO_CLIP: {
      return {
        ...state,
        selectedAudioClip: action.selectedAudioClip
      };
    }

    case SET_ACTIVE_SCENE: {
      return setActiveSceneReducer(state, action);
    }

    case EDITOR_SET_EDIT_PANEL_MODE: {
      if (action.mode === 'audio') {
        return {
          ...state,
          activeElements: []
        };
      }
      return {
        ...state,
        selectedAudioClip: null
      };
    }

    case DELETE_SELECTED_SCENES:
    case DELETE_MULTIPLE_ELEMENTS_BY_ID: {
      return {
        ...state,
        activeElements: []
      };
    }

    case DUPLICATE_SCRIBE: {
      return {
        ...state,
        duplicatingScribe: true
      };
    }

    case DUPLICATE_SCRIBE_START: {
      return {
        ...state,
        loadingScribes: [action.loadingScribe, ...state.loadingScribes]
      };
    }

    case DUPLICATE_SCRIBE_SUCCESS: {
      return {
        ...state,
        duplicatingScribe: true,
        homepageProjects: [action.duplicatedProject, ...state.homepageProjects],
        loadingScribes: state.loadingScribes.filter(scribe => filterTemporaryDuplicateProject(scribe, action.loadingId))
      };
    }

    case DUPLICATE_SCRIBE_FAILED: {
      return {
        ...state,
        duplicatingScribe: true,
        loadingScribes: state.loadingScribes.filter(scribe => filterTemporaryDuplicateProject(scribe, action.loadingId))
      };
    }

    case ADD_TO_SELECTED_SCENES: {
      const selectedScenesCopy = state.selectedSceneIds.slice() as string | Array<string>;
      const newSelectedScenes = typeof selectedScenesCopy === 'string' ? [selectedScenesCopy] : selectedScenesCopy;

      if (newSelectedScenes.indexOf(action.sceneId) === -1) newSelectedScenes.push(action.sceneId);

      return {
        ...state,
        selectedSceneIds: newSelectedScenes
      };
    }

    case UPDATE_LAST_SELECTED_SCENE: {
      const sceneIsInSelection = state.selectedSceneIds.includes(action.sceneId);
      return {
        ...state,
        lastSelectedScene: sceneIsInSelection ? action.sceneId : state.lastSelectedScene
      };
    }

    case CREATE_SCRIBE_FROM_TEMPLATE_SUCCESS: {
      if (!action.project) return state;

      const activeScene = getActiveScene(action.project);
      const homepageProjects = [createHomepageProjectFromProject(action.project), ...state.homepageProjects];

      return {
        ...state,
        allScribes: [action.project, ...state.allScribes],
        selectedSceneIds: activeScene ? [activeScene.id] : [],
        lastSelectedScene: activeScene ? activeScene.id : null,
        homepageProjects
      };
    }

    case CREATE_ELEMENT: {
      return {
        ...state,
        selectedSceneIds: [],
        lastSelectedScene: null
      };
    }
    case FILTER_SCRIBES_BY_CANVAS_SIZE: {
      return {
        ...state,
        canvasSizeFilter: action.size,
        // Reset allProjectsLoaded flag when switching back to 'allSizes'
        allProjectsLoaded: action.size === 'allSizes' ? false : state.allProjectsLoaded
      };
    }
    case FILTER_SCRIBES_BY_STATUS: {
      return { ...state, statusFilter: action.status };
    }
    case FILTER_SCRIBES_BY_SEARCH_TERM: {
      return { ...state, projectSearchTerm: action.term };
    }

    case UPDATE_PROJECT_TITLE: {
      return renameProjectReducer(state, action.scribeId, action.title);
    }

    case UPDATE_PROJECT_TITLE_FAILED: {
      return renameProjectReducer(state, action.scribeId, action.previousTitle);
    }

    case LOAD_SCRIBE_BY_ID_SUCCESS: {
      const existingIndex = state.allScribes.findIndex(scribe => scribe.id === action.scribe.id);

      if (existingIndex === -1) {
        return {
          ...state,
          firstLoad: true,
          allScribes: [...state.allScribes, action.scribe]
        };
      } else {
        const allScribes = cloneDeep(state.allScribes);
        allScribes[existingIndex] = action.scribe;

        return {
          ...state,
          firstLoad: true,
          selectedAudioClip: null,
          allScribes
        };
      }
    }

    case SET_SELECTED_TEXT_COLOR: {
      return {
        ...state,
        selectedTextColor: action.color
      };
    }

    case SET_CUSTOM_TEXT_STYLE: {
      return {
        ...state,
        customTextStyleToBeSet: action.style
      };
    }

    case SET_IS_TEXTBOX_EDITING: {
      return {
        ...state,
        isTextboxEditing: action.isEditing
      };
    }

    case GET_VIDEO_DOWNLOAD_URL_SUCCESS: {
      return {
        ...state,
        homepageProjects: state.homepageProjects.map(project =>
          project.id === action.scribeId ? { ...project, downloadReady: true } : project
        )
      };
    }

    case UPDATE_SCRIBE_SUCCESS: {
      return {
        ...state,
        homepageProjects: state.homepageProjects.map(project =>
          project.id === action.scribe.projectDataId ? { ...project, downloadReady: false } : project
        )
      };
    }

    default:
      return state;
  }
}
